/*
 * Authors: mariushirjanu
 * Created: 07/06/2023
 */
import * as _ from 'underscore';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams, IRowNode } from 'ag-grid-community';
import { FieldBuilder, FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '../../formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { CSS_ACTIONS_COLUMN, IFormGridColDef, IFormGridColDefParam } from '../../form-grid/form-grid.component';
import { GaValidateService } from '../../formly/ga-validate.service';
import { AgCellEditorService } from './ag-cell-editor.service';
import { IObject } from '@type_definitions/datatypes';
import { GaMessageService } from '../../message/ga-message.service';

const KEY_UP:number = 38;
const KEY_DOWN:number = 40;
const KEY_ESCAPE:number = 27;

@Component({
  selector: 'ag-cell-editor',
  template: `
    <div #formContainer>
      <formly *ngIf="!isActionCell; else actionCell" [form]="form" [fields]="fields" [model]="model"></formly>
    </div>

    <ng-template #actionCell class="ag-cell">
      <button *ngIf="isActionColumnDoubleWidth" class="btn btn-danger btn-sm btn-cancel" title="Cancel" (click)="onCancel()">
        <i class="fa fa-remove"></i>
      </button>
      <button class="btn btn-success btn-sm btn-save" title="Save" (click)="onSave()">
        <i class="fa fa-check"></i>
      </button>
    </ng-template>
  `
})
export class AgCellEditorComponent implements ICellEditorAngularComp, OnDestroy {
  @ViewChild('formContainer', { static: true }) public formContainer:ElementRef<HTMLFormElement>;

  private params!:ICellEditorParams;
  private cellEditorParams:IFormGridColDefParam;

  public fields:IExtraFieldConfigurationObject[];
  public fullRowFields:IExtraFieldConfigurationObject[];
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:IObject = <any>{};

  public isActionCell:boolean; // true when the editing cell has buttons for actions
  private key:string; // the key of the field created in the cell
  public isActionColumnDoubleWidth:boolean; // to determine if both cancel and save buttons are displayed

  constructor(
    private formlyService:GaFormlyService,
    private validateService:GaValidateService,
    private agCellEditorService:AgCellEditorService,
    private messageService:GaMessageService
  ) {
    /***/
  }

  public agInit(params:ICellEditorParams):void {
    // init parameters
    this.params = params;
    this.cellEditorParams = params.colDef.cellEditorParams;
    this.isActionCell = this.cellEditorParams.isActionCell;
    this.isActionColumnDoubleWidth = this.cellEditorParams.isActionColumnDoubleWidth;
    this.key = params.colDef?.field || 'inputValue';
    this.agCellEditorService.rowData = _.clone(params.data);

    if(!this.isActionCell){
      this.model[this.key] = this.params.value;
      this.fields = this.createFields();
      this.setFocusedCellInput(this.focusedColumn);
      this.listen();
    } else {
      // the cell is used for row actions
      this.fullRowFields = this.createFullRowFields();
    }
  }

  public getValue():any {
    return this.params.colDef?.cellEditorParams?.valueFormatter ? this.params.colDef?.cellEditorParams?.valueFormatter(this.model[this.key]) : this.model[this.key];
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const cellEditorParams:IFormGridColDefParam = this.params.colDef.cellEditorParams;

    const field:IExtraFieldConfigurationObject = this.createField(this.key, this.params.colDef.headerName || this.params.colDef.field, cellEditorParams)
      .build();

    if(cellEditorParams.optionsFn){
      this.formlyService.fillField(field, cellEditorParams.optionsFn());
    }

    return [ field ];
  }

  // eslint-disable-next-line no-restricted-syntax
  public onCancel():void {
    this.cellEditorParams.onCancelEditing(this.params.rowIndex);
  }

  // eslint-disable-next-line no-restricted-syntax
  public onSave():void {
    this.validateService.validateFields(this.fullRowFields, this.agCellEditorService.rowData, true)
      .then(() => {
        // save changes and stop editing
        this.params.api.stopEditing(false);
      })
      .catch((errorMessages:string[]) => {
        this.messageService.showErrorsDialog(errorMessages);
        return errorMessages;
      });
  }

  // create a listener for each cell form change to be able to sync the fields model with fullRowFields model
  private listen():void {
    // update service when the model changes
    this.form.valueChanges.subscribe((model:IObject) => {
      this.agCellEditorService.rowData[this.key] = model[this.key];
    });

    $(this.params.eGridCell).bind('keydown', this.onKeyDown);
  }


  // -----------------------------------------------------------------------------


  private createFullRowFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];
    const columnDefs:IFormGridColDef[] = this.params.api.getColumnDefs();

    _.each(columnDefs, (colDef:IFormGridColDef) => {
      const cellEditorParams:IFormGridColDefParam = colDef.cellEditorParams;
      if(!cellEditorParams.isActionCell) {
        fields.push(
          this.createField(colDef.field, colDef.headerName || colDef.field, cellEditorParams)
            .build()
        );
      }
    });

    return fields;
  }

  private createField(key:string, label:string, cellEditorParams:IFormGridColDefParam):FieldBuilder {
    return this.formlyService.aField(cellEditorParams.type || FormlyType.INPUT, key, label)
      .withRequired(cellEditorParams.required)
      .withInputType(cellEditorParams.inputType)
      .withValueClass('col-sm-12')
      .withLabelClass('d-none')
      .withOptions(cellEditorParams.options)
      .withFormat(cellEditorParams.format)
      .withAddOn(cellEditorParams.addOn)
      .withLabelProp(cellEditorParams.labelProp)
      .withValueProp(cellEditorParams.valueProp)
      .withMinDate(cellEditorParams.minDate)
      .withMaxDate(cellEditorParams.maxDate)
      .withExtraOptions({
        templateOptions: {
          min: cellEditorParams.min
        }
      });
  }

  private setFocusedCellInput(colDef:IFormGridColDef):void {
    if (this.params.colDef.field === colDef?.field) {
      // wait a moment for input field to render in UI
      setTimeout(() => {
        (<HTMLInputElement>this.formContainer.nativeElement.querySelectorAll('input, select').item(0))?.focus();
      });
    }
  }

  private onKeyDown = (event:JQueryEventObject):void => {
    if (event.which === KEY_UP && this.params.node.rowIndex > 0) {
      const prevIndex:number = this.params.node.rowIndex - 1;
      const prevNode:IRowNode = this.params.api.getDisplayedRowAtIndex(prevIndex);

      // we should get index of master row (not the detil row)
      const prevMasterIndex:number = prevNode.detail? prevNode.parent.rowIndex : prevIndex;

      this.params.api.stopEditing();
      this.params.api.startEditingCell({
        rowIndex: prevMasterIndex,
        colKey: this.params.colDef.field
      });

      this.params.api.setFocusedCell(prevMasterIndex, this.params.colDef.field);
    } else if (event.which === KEY_DOWN && this.params.node.rowIndex < (this.params.api.getDisplayedRowCount() - 1)) {
      const nextIndex:number = this.params.node.rowIndex + 1;
      const nextNode:IRowNode = this.params.api.getDisplayedRowAtIndex(nextIndex);

      // we should get index of master row (not the detail row)
      const nextMasterIndex:number = nextNode.detail? (nextNode.rowIndex + 1) : nextIndex;

      this.params.api.stopEditing();
      this.params.api.startEditingCell({
        rowIndex: nextMasterIndex,
        colKey: this.params.colDef.field
      });

      this.params.api.setFocusedCell(nextMasterIndex, this.params.colDef.field);
    } else if (event.which === KEY_ESCAPE) {
      this.params.api.stopEditing();
    }
  };

  // from ag-grid
  public focusIn():void {
    // set focus cell for tabbing if its next or previous index
    this.params.api.setFocusedCell(this.params.node.rowIndex, this.params.colDef.field);
    this.setFocusedCellInput(this.params.colDef);
  }


  // get the column to focus on
  private get focusedColumn():IFormGridColDef {
    // take the column that was clicked, only if exists and it's editable
    let focusedColumn:IFormGridColDef = this.params.api.getFocusedCell()?.column.getColDef();
    const isEditable:boolean = _.isFunction(focusedColumn?.editable) ? (<any>focusedColumn)?.editable(this.params) : focusedColumn?.editable;
    if(!isEditable){
      // take the first editable column except the column for actions
      focusedColumn = _.find(this.params.api.getColumnDefs(), (colDef:IFormGridColDef) => _.isFunction(colDef?.editable) ? (<any>colDef).editable(this.params) : colDef.editable && colDef.cellClass !== CSS_ACTIONS_COLUMN);
    }
    return focusedColumn;
  }

  public ngOnDestroy():void {
    // we should unbind this to prevent lagging when triggering key up or key down
    $(this.params.eGridCell).unbind('keydown');
  }
}